import { useNavigate } from 'react-router-dom'
import { Utensils } from 'lucide-react'
import { useStore } from '../store/useStore'
import { useMyOrders } from '../hooks/useMyOrders'
import { useI18n } from '../i18n/useI18n'
import { isSelfOrderEnabled } from '../lib/features'

/**
 * #947 (管理番号_004) Persistent "you're seated at table N" banner.
 *
 * Surfaces the customer's active dine-in session — an open (unpaid,
 * non-cancelled) dine-in order that carries a table number — so the seat
 * stays visible across the app (Home / Explore / Profile) until the bill is
 * paid, not just on the store page. Tapping returns to the store's menu with
 * the table preserved so they can keep ordering without re-scanning the QR.
 *
 * The seat is derived from the order (server truth), so it ends exactly when
 * the session ends: once paid (or cancelled) there's no open order → the
 * banner disappears. Renders nothing when there's no active table session.
 *
 * Mounted inside TabBar so it shows on every main customer page.
 */
export default function ActiveTableBanner() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const stores = useStore((s) => s.stores)
  const { orders } = useMyOrders()

  if (!isSelfOrderEnabled()) return null

  // orders are newest-first; the latest open dine-in order with a table is the
  // current seat. pending_approval counts (the visit has started); paid /
  // cancelled orders end the session.
  const seated = orders.find(
    (o) => o.orderType !== 'delivery' && !o.paid && o.status !== 'cancelled' && !!o.tableNumber,
  )
  if (!seated) return null

  const storeName = stores.find((s) => s.id === seated.storeId)?.name || ''

  return (
    <button
      onClick={() => navigate(`/stores/${seated.storeId}`)}
      className="fixed left-1/2 -translate-x-1/2 w-full max-w-[430px] z-40 px-3"
      style={{ bottom: 83 }}
      aria-label={t('activeTable.banner', { table: seated.tableNumber ?? '' })}
    >
      <div className="mb-2 rounded-xl bg-blue-600 text-white px-4 py-2 shadow-lg flex items-center justify-center gap-2 tap-effect active:scale-[0.99] transition-transform">
        <Utensils size={14} className="shrink-0" />
        <span className="font-body text-xs font-semibold truncate">
          {storeName
            ? t('activeTable.bannerWithStore', { store: storeName, table: seated.tableNumber ?? '' })
            : t('activeTable.banner', { table: seated.tableNumber ?? '' })}
        </span>
      </div>
    </button>
  )
}
